import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  syncSavedTrips,
  setSavedTripsUser,
  onSignOutKeepTrips,
} from "@/utils/savedTrips";

/**
 * Keeps saved trips reconciled with the user's account.
 *
 * Runs on: sign-in, app foreground, and regaining connectivity. Each of those
 * is a moment where the other device may have changed something. It is
 * deliberately quiet -- no spinner, no toast on success. Sync that announces
 * itself is sync the user has to think about.
 *
 * Mount once, high in the tree (App.tsx or Index.tsx).
 */
export function useSavedTripsSync(userId: string | null | undefined) {
  const running = useRef(false);
  const lastRun = useRef(0);

  useEffect(() => {
    setSavedTripsUser(userId ?? null);

    if (!userId) {
      onSignOutKeepTrips();
      return;
    }

    const run = async (reason: string) => {
      // Coalesce: foreground + online can fire together.
      if (running.current) return;
      if (Date.now() - lastRun.current < 3_000) return;
      running.current = true;
      try {
        const r = await syncSavedTrips(userId);
        lastRun.current = Date.now();
        if (r.pushed || r.pulled) {
          console.info(
            `[savedTripsSync] ${reason}: pushed ${r.pushed}, pulled ${r.pulled}`,
          );
        }
      } finally {
        running.current = false;
      }
    };

    void run("sign-in");

    const onVisible = () => {
      if (document.visibilityState === "visible") void run("foreground");
    };
    const onOnline = () => void run("online");

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", onOnline);

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", onOnline);
    };
  }, [userId]);
}

/**
 * One-shot variant for surfaces that do not already have the user in hand
 * (CarPlay bridge, deep-link entry). Resolves the session itself.
 */
export async function syncSavedTripsNow(): Promise<void> {
  const { data } = await supabase.auth.getSession();
  const uid = data.session?.user?.id;
  if (uid) await syncSavedTrips(uid);
}
